import React, { HTMLAttributes, useEffect, useState } from 'react';
import { IToolbar } from './Toolbar.interface';
import { ThumbsFeedback } from '@zurich-zurichat/thumbs-feedback';
import { sendThumbsFeedback } from 'integration';
import {
  getBusinessArea,
  getGeneratingExtraction,
  getHash,
  getRegenerateExtraction,
  getService,
  useAppSelector
} from 'app/redux';
import { IThumbsFeedback, ServicesData } from 'app/utils';
import { Button } from '@zurich-primitives/button';
import { Copy } from '@zurich-data-dsm-icons/action.copy';
import { Check } from '@zurich-data-dsm-icons/action.check';
import { Trash } from '@zurich-data-dsm-icons/action.trash';
import { Pencil } from '@zurich-data-dsm-icons/action.pencil';
import styles from './Toolbar.module.scss';
import { SERVICE_NAMES } from 'app/utils/consts';

export const Toolbar: React.FC<IToolbar & HTMLAttributes<HTMLDivElement>> = ({
  title,
  onEdit,
  onDelete,
  selectedType,
  ...rest
}) => {
  const currentService = useAppSelector(getService);
  const currentBusinessArea = useAppSelector(getBusinessArea);
  const currentHash = useAppSelector(getHash);
  const [copyIcon, setCopyIcon] = React.useState<React.JSX.Element>(<Copy />);
  const [activeFeatures, setActiveFeatures] = React.useState<string[]>([]);
  const [key, setKey] = useState(0);
  const regenerateExtraction = useAppSelector(getRegenerateExtraction);
  const isGeneratingExtraction = useAppSelector(getGeneratingExtraction);

  const copyMessage = async (): Promise<void> => {
    const fieldElement = document.getElementById(`field-${title}`);
    const textAreaElement = fieldElement?.getElementsByTagName('textarea')[0];

    await navigator.clipboard.writeText(
      (textAreaElement as HTMLTextAreaElement).value ?? ''
    );
    setCopyIcon(<Check />);
    window.setTimeout(() => setCopyIcon(<Copy />), 3000);
  };

  const handleEditField = (): void => {
    onEdit();
  };
  const handleDeleteField = (): void => {
    onDelete();
  };

  const onlyCopyFeatures = [
    SERVICE_NAMES.FNOL_CAPTURING,
    SERVICE_NAMES.CALL_SUMMARY,
    SERVICE_NAMES.INVOICE_EXTRACTOR,
    SERVICE_NAMES.COVERAGE_CHECK,
    SERVICE_NAMES.RETENTION_CALL,
    SERVICE_NAMES.COVERAGE_CHECK_V2
  ];

  useEffect(() => {
    if (isGeneratingExtraction || regenerateExtraction) {
      setKey((prevKey) => prevKey + 1);
    }
  }, [isGeneratingExtraction, regenerateExtraction]);

  useEffect(() => {
    if (currentService != null) {
      switch (ServicesData[currentService].ExtractionType) {
        case 'extraction':
          if (currentService === SERVICE_NAMES.ID_EXTRACT_PRO) {
            setActiveFeatures(['copy', 'edit', 'delete']);
            break;
          }
          setActiveFeatures(['thumbs', 'copy', 'edit', 'delete']);
          break;
        case 'summary':
        case 'email-summary':
        case 'translation':
          setActiveFeatures(['copy']);
          break;
      }
      if (onlyCopyFeatures.includes(currentService))
        setActiveFeatures(['copy']);
    }
  }, [currentService]);

  return (
    <div className={styles.toolbar} {...rest}>
      {activeFeatures.includes('thumbs') && (
        <ThumbsFeedback
          key={key}
          onThumbUp={() => {
            if (currentHash != null) {
              const body: IThumbsFeedback = {
                hash: currentHash,
                key: title,
                feedback: 'positive'
              };
              if (selectedType) {
                body.type = selectedType;
              }
              if (currentBusinessArea != null && currentService != null)
                sendThumbsFeedback(currentBusinessArea, currentService, body);
            }
          }}
          onThumbDown={() => {
            if (currentHash != null) {
              const body: IThumbsFeedback = {
                hash: currentHash,
                key: title,
                feedback: 'negative'
              };
              if (selectedType) {
                body.type = selectedType;
              }
              if (currentBusinessArea != null && currentService != null)
                sendThumbsFeedback(currentBusinessArea, currentService, body);
            }
          }}
        />
      )}
      {activeFeatures.includes('copy') && (
        <Button
          variant="tertiary"
          aria-label="Copy message to the clipboard"
          className={styles.copy}
          onClick={async () => await copyMessage()}
        >
          <Button.Icon>{copyIcon}</Button.Icon>
        </Button>
      )}
      {activeFeatures.includes('edit') && (
        <Button
          variant="tertiary"
          aria-label="Edit field"
          className={styles.copy}
          onClick={handleEditField}
        >
          <Button.Icon>
            <Pencil />
          </Button.Icon>
        </Button>
      )}
      {activeFeatures.includes('delete') && (
        <Button
          variant="tertiary"
          aria-label="Delete this field"
          className={styles.copy}
          onClick={handleDeleteField}
        >
          <Button.Icon>
            <Trash />
          </Button.Icon>
        </Button>
      )}
    </div>
  );
};
